import axios from "axios";
import { useEffect, useState } from "react";
import { Plus, Loader2, Save, Trash2, Pencil, Tag, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const COLOR_PALETTE = [
  "slate", "blue", "violet", "amber", "emerald", "rose",
  "indigo", "sky", "lime", "fuchsia", "teal", "orange",
];

const COLOR_CLASSES = {
  slate:   { bar: "bg-slate-400",   chip: "bg-slate-100 text-slate-700",   ring: "ring-slate-300" },
  blue:    { bar: "bg-blue-500",    chip: "bg-blue-100 text-blue-700",     ring: "ring-blue-300" },
  violet:  { bar: "bg-violet-500",  chip: "bg-violet-100 text-violet-700", ring: "ring-violet-300" },
  amber:   { bar: "bg-amber-500",   chip: "bg-amber-100 text-amber-700",   ring: "ring-amber-300" },
  emerald: { bar: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-700", ring: "ring-emerald-300" },
  rose:    { bar: "bg-rose-500",    chip: "bg-rose-100 text-rose-700",     ring: "ring-rose-300" },
  indigo:  { bar: "bg-indigo-500",  chip: "bg-indigo-100 text-indigo-700", ring: "ring-indigo-300" },
  sky:     { bar: "bg-sky-500",     chip: "bg-sky-100 text-sky-700",       ring: "ring-sky-300" },
  lime:    { bar: "bg-lime-500",    chip: "bg-lime-100 text-lime-700",     ring: "ring-lime-300" },
  fuchsia: { bar: "bg-fuchsia-500", chip: "bg-fuchsia-100 text-fuchsia-700", ring: "ring-fuchsia-300" },
  teal:    { bar: "bg-teal-500",    chip: "bg-teal-100 text-teal-700",     ring: "ring-teal-300" },
  orange:  { bar: "bg-orange-500",  chip: "bg-orange-100 text-orange-700", ring: "ring-orange-300" },
};

const ColorSwatch = ({ color, selected, onClick, ariaLabel }) => (
  <button
    type="button"
    aria-label={ariaLabel}
    onClick={onClick}
    data-testid={`color-swatch-${color}`}
    className={`w-7 h-7 rounded-md ${COLOR_CLASSES[color]?.bar || "bg-slate-300"} transition-all hover:scale-110 ${
      selected ? `ring-2 ring-offset-2 ${COLOR_CLASSES[color]?.ring || "ring-slate-400"} scale-110` : ""
    }`}
  />
);

const emptyDraft = { label: "", color: "slate", order: 100 };

const StatusesPanel = () => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // "new" for new entry
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/registry/statuses`);
      setStatuses(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setDraft({ ...emptyDraft, order: (statuses[statuses.length - 1]?.order ?? 100) + 10 });
    setEditingId("new");
  };

  const startEdit = (s) => {
    setDraft({ label: s.label, color: s.color, order: s.order });
    setEditingId(s.id);
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(emptyDraft);
  };

  const handleSave = async () => {
    if (!draft.label.trim()) {
      toast.error("O nome do status é obrigatório.");
      return;
    }
    setSaving(true);
    try {
      if (editingId === "new") {
        await axios.post(`${API}/registry/statuses`, draft);
        toast.success("Status criado.");
      } else {
        await axios.patch(`${API}/registry/statuses/${editingId}`, draft);
        toast.success("Status atualizado.");
      }
      cancel();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (s) => {
    if (s.is_system) {
      toast.error(`"${s.label}" é status do sistema e não pode ser removido.`);
      return;
    }
    if (s.candidates_count > 0) {
      toast.error(
        `Não é possível remover: ${s.candidates_count} candidato(s) com este status.`
      );
      return;
    }
    if (!window.confirm(`Remover o status "${s.label}"? Isso não pode ser desfeito.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/registry/statuses/${s.id}`);
      toast.success("Status removido.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao remover");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="statuses-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="heading text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
            <Tag className="w-5 h-5 text-brand-blue" />
            Status do Pipeline
          </h2>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">
            Configure as colunas do Kanban e as opções do dropdown de status no perfil de cada candidato. Status do sistema podem ser renomeados, mas não removidos.
          </p>
        </div>
        <Button
          onClick={startNew}
          data-testid="status-new-btn"
          disabled={!!editingId}
          className="bg-brand-blue hover:bg-brand-blue-dark text-white"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Novo status
        </Button>
      </div>

      {editingId === "new" && (
        <StatusEditor
          draft={draft}
          setDraft={setDraft}
          onSave={handleSave}
          onCancel={cancel}
          saving={saving}
          isNew
        />
      )}

      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
        {statuses.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-500">
            Nenhum status cadastrado.
          </div>
        ) : (
          statuses.map((s) => {
            const palette = COLOR_CLASSES[s.color] || COLOR_CLASSES.slate;
            const editing = editingId === s.id;
            return (
              <div key={s.id} data-testid={`status-row-${s.id}`} className="p-4">
                {editing ? (
                  <StatusEditor
                    draft={draft}
                    setDraft={setDraft}
                    onSave={handleSave}
                    onCancel={cancel}
                    saving={saving}
                    isProtected={s.is_system}
                  />
                ) : (
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className={`block w-1.5 h-7 rounded-full ${palette.bar} shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-900">
                          {s.label}
                        </span>
                        {s.is_system && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 uppercase tracking-wider"
                            title="Status do sistema — não pode ser removido"
                          >
                            <ShieldCheck className="w-3 h-3" /> Sistema
                          </span>
                        )}
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${palette.chip}`}
                        >
                          {s.candidates_count}{" "}
                          candidato{s.candidates_count === 1 ? "" : "s"}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
                        chave: {s.key} · ordem: {s.order}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startEdit(s)}
                        disabled={!!editingId}
                        data-testid={`status-edit-${s.id}`}
                        className="text-xs h-8"
                      >
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(s)}
                        disabled={s.is_system || s.candidates_count > 0 || !!editingId}
                        data-testid={`status-delete-${s.id}`}
                        className={`text-xs h-8 ${
                          s.is_system || s.candidates_count > 0
                            ? "opacity-40 cursor-not-allowed"
                            : "text-rose-600 hover:bg-rose-50 hover:border-rose-300"
                        }`}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Remover
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const StatusEditor = ({ draft, setDraft, onSave, onCancel, saving, isNew, isProtected }) => {
  const update = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  return (
    <div className="bg-slate-50/70 border border-slate-200 rounded-lg p-4 space-y-3">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px] space-y-1.5">
          <Label htmlFor="status-label" className="text-xs">
            Nome {isNew ? "(será usado no Kanban e no dropdown)" : ""}
          </Label>
          <Input
            id="status-label"
            data-testid="status-label-input"
            value={draft.label}
            onChange={(e) => update("label", e.target.value)}
            placeholder="Ex.: Em negociação"
            autoFocus
          />
          {isProtected && (
            <p className="text-[11px] text-amber-700 inline-flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Status do sistema: renomeie à vontade, mas a chave
              interna não muda.
            </p>
          )}
        </div>
        <div className="w-28 space-y-1.5">
          <Label htmlFor="status-order" className="text-xs">Ordem</Label>
          <Input
            id="status-order"
            type="number"
            data-testid="status-order-input"
            value={draft.order}
            onChange={(e) => update("order", parseInt(e.target.value || "0", 10))}
          />
        </div>
      </div>
      <div>
        <Label className="text-xs mb-1.5 block">Cor da coluna</Label>
        <div className="flex flex-wrap gap-2">
          {COLOR_PALETTE.map((c) => (
            <ColorSwatch
              key={c}
              color={c}
              selected={draft.color === c}
              onClick={() => update("color", c)}
              ariaLabel={c}
            />
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>
          <X className="w-3.5 h-3.5 mr-1" /> Cancelar
        </Button>
        <Button
          size="sm"
          onClick={onSave}
          disabled={saving || !draft.label.trim()}
          data-testid="status-save-btn"
          className="bg-brand-blue hover:bg-brand-blue-dark text-white"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
          Salvar
        </Button>
      </div>
    </div>
  );
};

export default StatusesPanel;
